// ── LIKVIDITETSMATRIS ─────────────────────────────────────────────
// Budgetmatrisen svarar på "tjänar vi pengar", den här på "räcker
// pengarna". Alla rörelserader HÄRLEDS ur budgeten via betalningsregler
// (personal +1 mån, leverantörer +1 mån osv) — de matas aldrig in igen,
// annars glider de två matriserna isär.
//
// Egen inmatning finns bara för det som saknar motsvarighet i resultatet:
// investeringar, amorteringar, moms, skatt och extraordinära poster.
//
// Förskotten flyttar kassa från intjänandemånad till betalmånad utan att
// ändra årssumman — se beraknaLikviditet i budgetModel.js.
import React, { useMemo, useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getDailyUnits } from '../qlik';
import {
  RADER, MANAD_LABELS, BETALREGLER, DEFAULT_BETALREGEL,
  beraknaLikviditet, KOSTNADSGRUPPER, raManader,
} from './budgetModel';

const ACCENT = '#44365C';
const kr0 = (v) => Math.round(v / 1000);
const visa = (v) => (v ? kr0(v).toLocaleString('sv-SE') : '–');
const raLabel = (r) => `RÅ ${r.slice(0, 2)}/${r.slice(2)}`;
// "2728" → "2627" (föregående räkenskapsår)
const foregaendeRa = (r) => String(Number(r.slice(0, 2)) - 1).padStart(2, '0') + r.slice(0, 2);

export default function LikviditetsMatris({
  bolag, ra, manader, rader, likvid, last, onSetCell, onSetLikvid,
}) {
  const [visaRegler, setVisaRegler] = useState(false);
  const [nyForskott, setNyForskott] = useState(null);
  const [foreVersioner, setForeVersioner] = useState(null); // föregående RÅ:s budgetversioner (null = ej laddat)
  const [foreVersion, setForeVersion] = useState('');
  const [hamtarIng, setHamtarIng] = useState(false);
  const [hamtar, setHamtar] = useState(false);

  // Månaden före räkenskapsårets start — dess löner betalas i årets första
  // månad men ligger i föregående års budget, så de måste hämtas separat.
  const foreMan = backat(manader[0], 1);

  // ── Ingående saldon ur föregående års budget ────────────────────
  // Kedjan som gör en 24-månaders likviditetsbudget möjlig: 27/28 börjar
  // exakt där 26/27 slutar. Versionerna hämtas vid montering så att man
  // kan välja underlag (B0 = fastställd, P-versioner = senare prognoser).
  const foreRa = foregaendeRa(ra);
  useEffect(() => {
    if (last) return undefined;
    let live = true;
    (async () => {
      const q1 = query(collection(db, 'intel_budget'), where('bolag', '==', bolag), where('ra', '==', foreRa));
      const snap = await getDocs(q1);
      const vs = snap.docs.map((d) => d.data().version).sort((a, b) => a.localeCompare(b));
      if (!live) return;
      setForeVersioner(vs);
      // Senaste versionen = färskaste synen på var året landar.
      setForeVersion(vs[vs.length - 1] || '');
    })().catch((e) => console.error('likvid: föregående versioner:', e));
    return () => { live = false; };
  }, [bolag, foreRa, last]);

  // Utgående bank + den svans av betalningar som reglerna sköt förbi
  // årsskiftet. Svansen finns redan i flödet men summeras aldrig av
  // beraknaLikviditet, som bara går igenom sina tolv månader.
  const hamtaIngaende = async () => {
    setHamtarIng(true);
    try {
      const q1 = query(collection(db, 'intel_budget'), where('bolag', '==', bolag), where('ra', '==', foreRa));
      const snap = await getDocs(q1);
      const kalla = snap.docs.map((d) => d.data()).find((d) => d.version === foreVersion);
      if (!kalla) {
        window.alert(`Ingen budget finns för ${raLabel(foreRa)} ${foreVersion} — lägg den först.`);
        return;
      }
      const foreManader = raManader(foreRa);
      const sista = foreManader[foreManader.length - 1];
      const m = beraknaLikviditet({
        bolag, rader: kalla.rader || {}, likvid: kalla.likvid || {}, manader: foreManader,
      });
      const bank = Math.round(m.perManad[m.perManad.length - 1].utgaende);
      const gruppAv = Object.fromEntries((RADER[bolag] || []).map((r) => [r.id, r.grupp]));
      let skulder = 0; let personal = 0; let fordringar = 0;
      for (const [radId, byYm] of Object.entries(m.flode)) {
        for (const [ym, kr] of Object.entries(byYm)) {
          if (ym <= sista) continue;              // ligger inom året, redan räknat
          if (kr < 0) { if (gruppAv[radId] === 'personal') personal -= kr; else skulder -= kr; }
          else fordringar += kr;                  // inbetalning efter årsskiftet
        }
      }
      skulder = Math.round(skulder); personal = Math.round(personal);
      const rad = (etikett, fran, till) => `  ${etikett}: ${visa(fran)} → ${visa(till)} tkr`;
      const text = `Hämta ingående saldon ur ${raLabel(foreRa)} ${foreVersion}\n\n`
        + `${rad('Ingående bank', likvid?.ingaendeBank, bank)}\n`
        + `${rad('Ingående lev.skulder', likvid?.ingaendeSkulder, skulder)}\n`
        + `${rad('Ingående personalskuld', likvid?.ingaendePersonal, personal)}\n\n`
        + `Banken är ${raLabel(foreRa)}:s BUDGETERADE utgående saldo, inte ett utfall — `
        + 'hela årets avvikelse slår rakt in i den. Momsen ingår inte i modellen.\n\n'
        + (fordringar
          ? `OBS: ${visa(fordringar)} tkr INBETALNINGAR faller också efter årsskiftet `
            + '(en intäktsrad har en betalningsregel med förskjutning). De tas INTE med — '
            + 'modellen har ingen ingående fordran. Lägg in dem som en likvidpost om de ska med.\n\n'
          : '')
        + 'Skriva över?';
      if (!window.confirm(text)) return;
      onSetLikvid({
        ...likvid, ingaendeBank: bank, ingaendeSkulder: skulder, ingaendePersonal: personal,
      });
    } catch (e) {
      window.alert('Kunde inte hämta: ' + (e?.message || e));
    } finally { setHamtarIng(false); }
  };

  const hamtaPersonal = async () => {
    setHamtar(true);
    try {
      const rows = await getDailyUnits();
      const pre = `${foreMan.slice(0, 4)}-${foreMan.slice(4)}`;
      const summa = rows.reduce((s, r) => {
        if (!r.datum.startsWith(pre)) return s;
        const sc = r.enhet === 'Sportcenter';
        if (bolag === 'sc' ? !sc : sc) return s;
        return s + Math.abs(r.lon || 0);
      }, 0);
      onSetLikvid({ ...likvid, ingaendePersonal: Math.round(summa) });
    } catch (e) {
      window.alert('Kunde inte hämta ur Qlik: ' + e.message);
    } finally { setHamtar(false); }
  };

  const raderDef = RADER[bolag] || [];
  const modell = useMemo(
    () => beraknaLikviditet({ bolag, rader, likvid, manader }),
    [bolag, rader, likvid, manader],
  );
  const { flode, perManad, lagsta } = modell;

  // Avstämt banksaldo: default är senast avslutade månaden i året
  const avstamning = likvid?.avstamning || null;
  const avstDefaultYm = (() => {
    const d = new Date();
    const nuYm = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
    const avslutade = manader.filter((m) => m < nuYm);
    return avslutade.length ? avslutade[avslutade.length - 1] : manader[0];
  })();
  const avstMan = perManad.find((p) => p.avstamd) || null;

  const cellInp = {
    width: 56, border: '1px solid var(--border)', borderRadius: 5, padding: '2px 4px',
    fontSize: 11, textAlign: 'right', fontFamily: 'system-ui,sans-serif',
    background: last ? 'var(--panel)' : 'var(--card)', color: 'var(--text)',
  };
  const th = { fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', padding: '4px 3px', textAlign: 'right', fontFamily: 'system-ui,sans-serif' };
  const tdLabel = { fontSize: 11.5, padding: '2px 6px 2px 0', whiteSpace: 'nowrap', fontFamily: 'system-ui,sans-serif', position: 'sticky', left: 0, background: 'var(--card)', zIndex: 1 };
  const tdCalc = { fontSize: 11, padding: '3px 4px', textAlign: 'right', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap' };

  const satt = (falt, v) => onSetLikvid({ ...likvid, [falt]: v });
  const forskott = likvid?.forskott || [];

  // Härledd rad ur flödet (läsläge). Belopp är signerade: + in, − ut.
  const flodesRad = (rad, tecken) => {
    const v = flode[rad.id] || {};
    const summa = manader.reduce((s, m) => s + (v[m] || 0), 0);
    if (!summa && !manader.some((m) => v[m])) return null;
    const regelId = (likvid?.betalregler || {})[rad.id] || DEFAULT_BETALREGEL[rad.grupp] || 'samma';
    const regel = BETALREGLER.find((b) => b.id === regelId);
    return (
      <tr key={rad.id}>
        <td style={tdLabel}>
          {rad.label}
          {regel && regel.skift > 0 && (
            <span style={{ marginLeft: 5, fontSize: 9.5, color: 'var(--text-secondary)' }}>+{regel.skift}</span>
          )}
        </td>
        {manader.map((m) => (
          <td key={m} style={{ ...tdCalc, color: 'var(--text-secondary)' }}>{visa(tecken * (v[m] || 0))}</td>
        ))}
        <td style={{ ...tdCalc, fontWeight: 600 }}>{visa(tecken * summa)}</td>
      </tr>
    );
  };

  const summaRad = (label, varden, opts = {}) => (
    <tr style={{ background: opts.bg || 'transparent', fontWeight: 700 }}>
      <td style={{ ...tdLabel, background: opts.bg ? `linear-gradient(${opts.bg}, ${opts.bg}), var(--card)` : 'var(--card)', fontWeight: 700 }}>{label}</td>
      {varden.map((v, i) => (
        <td key={i} style={{ ...tdCalc, fontWeight: 700, color: opts.farga && v < 0 ? '#A32D2D' : 'inherit' }}>
          {visa(v)}
        </td>
      ))}
      <td style={{ ...tdCalc, fontWeight: 700 }}>{opts.utanSumma ? '–' : visa(varden.reduce((a, b) => a + b, 0))}</td>
    </tr>
  );

  // motpost-rader (Ränteintäkter) ligger i en kostnadsgrupp men är pengar
  // som kommer IN — visa dem bland inbetalningarna.
  const intaktRader = raderDef.filter((r) => r.grupp === 'intakt' || r.motpost);
  const kostnadRader = raderDef.filter((r) => KOSTNADSGRUPPER.has(r.grupp) && r.grupp !== 'avskr' && !r.motpost);
  // Likvidposter vars id slutar på _in är pengar som kommer in
  const likvidIn = raderDef.filter((r) => r.grupp === 'likvid' && r.id.endsWith('_in'));
  const likvidUt = raderDef.filter((r) => r.grupp === 'likvid' && !r.id.endsWith('_in'));

  const likvidRad = (r) => (
    <tr key={r.id}>
      <td style={tdLabel}>{r.label}</td>
      {manader.map((m) => (
        <td key={m} style={{ padding: '1px 2px' }}>
          <input type="number" style={cellInp} disabled={last}
            value={(rader[r.id] || {})[m] ? kr0(rader[r.id][m]) : ''} placeholder="0"
            onChange={(e) => onSetCell(r.id, m, e.target.value)} />
        </td>
      ))}
      <td style={{ ...tdCalc, fontWeight: 600 }}>
        {visa(manader.reduce((s, m) => s + ((rader[r.id] || {})[m] || 0), 0))}
      </td>
    </tr>
  );

  return (
    <>
      {/* Toppkort: utgångsläge och årets lägsta punkt */}
      <div className="card">
        <div className="card-title">Utgångsläge</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Ingående bank (tkr)</span>
            <input type="number" style={{ ...cellInp, width: 90 }} disabled={last}
              value={likvid?.ingaendeBank ? kr0(likvid.ingaendeBank) : ''}
              onChange={(e) => satt('ingaendeBank', Math.round((Number(e.target.value) || 0) * 1000))} />
            <Kvitto belopp={likvid?.ingaendeBank} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }} title="Junis leverantörsfakturor som betalas i juli. De ligger i förra årets budget och fångas inte av förskjutningen. Lönerna behöver du INTE lägga in här — de ligger redan i budgetens julirad.">
              Ingående lev.skulder (tkr)
            </span>
            <input type="number" style={{ ...cellInp, width: 90 }} disabled={last}
              value={likvid?.ingaendeSkulder ? kr0(likvid.ingaendeSkulder) : ''}
              onChange={(e) => satt('ingaendeSkulder', Math.round((Number(e.target.value) || 0) * 1000))} />
            <Kvitto belopp={likvid?.ingaendeSkulder} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }} title="Junis löner betalas i juli men ligger i föregående räkenskapsårs budget. Utan detta saknar juli helt personalkostnad.">
              Ingående personalskuld (tkr)
            </span>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="number" style={{ ...cellInp, width: 90 }} disabled={last}
                value={likvid?.ingaendePersonal ? kr0(likvid.ingaendePersonal) : ''}
                onChange={(e) => satt('ingaendePersonal', Math.round((Number(e.target.value) || 0) * 1000))} />
              {!last && bolag !== 'stigmyhrs' && (
                <button onClick={hamtaPersonal} disabled={hamtar}
                  title={`Hämtar ${manadNamn(foreMan)} ur Qlik`}
                  style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap' }}>
                  {hamtar ? '…' : `↻ ${manadNamn(foreMan)}`}
                </button>
              )}
            </div>
            <Kvitto belopp={likvid?.ingaendePersonal} />
          </label>
          {!last && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Kedja från föregående år</span>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <button onClick={hamtaIngaende} disabled={hamtarIng || !foreVersion}
                  title={foreVersion
                    ? `Utgående bank och obetald svans ur ${raLabel(foreRa)} ${foreVersion}`
                    : `Ingen sparad budget för ${raLabel(foreRa)}`}
                  style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: foreVersion ? 'pointer' : 'not-allowed', color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap' }}>
                  {hamtarIng ? 'Hämtar…' : `↩ Hämta ur ${raLabel(foreRa)}`}
                </button>
                {(foreVersioner || []).length > 1 && (
                  <select value={foreVersion} onChange={(e) => setForeVersion(e.target.value)}
                    style={{ border: '1px solid var(--border)', borderRadius: 5, padding: '3px 5px', fontSize: 11, background: 'var(--card)', color: 'var(--text)' }}>
                    {foreVersioner.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                )}
              </div>
              <span style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                {foreVersioner === null ? 'läser…'
                  : foreVersion ? `${raLabel(foreRa)}:s utgående läge`
                    : `ingen budget för ${raLabel(foreRa)}`}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 14, borderLeft: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}
              title="Verkligt banksaldo vid månadens utgång. Kurvan fortsätter därifrån i stället för från budgetens antagande.">
              Avstämt banksaldo (tkr)
            </span>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <select value={avstamning?.ym || avstDefaultYm} disabled={last}
                onChange={(e) => avstamning && satt('avstamning', { ...avstamning, ym: e.target.value })}
                style={{ border: '1px solid var(--border)', borderRadius: 5, padding: '3px 5px', fontSize: 11, background: 'var(--card)', color: 'var(--text)' }}>
                {manader.map((m, i) => <option key={m} value={m}>per {MANAD_LABELS[i]} {m.slice(2, 4)}</option>)}
              </select>
              <input type="number" style={{ ...cellInp, width: 90 }} disabled={last}
                value={avstamning ? kr0(avstamning.bank) : ''} placeholder="–"
                onChange={(e) => {
                  if (e.target.value === '') { const { avstamning: _bort, ...rest } = likvid || {}; onSetLikvid(rest); return; }
                  satt('avstamning', { ym: avstamning?.ym || avstDefaultYm, bank: Math.round(Number(e.target.value) * 1000) });
                }} />
            </div>
            <Kvitto belopp={avstamning?.bank} />
            {avstMan && (
              <span style={{ fontSize: 10.5, color: avstMan.justering < 0 ? '#A32D2D' : '#1D6A5F' }}>
                Modellen låg {visa(Math.abs(avstMan.justering))} tkr {avstMan.justering < 0 ? 'för högt' : 'för lågt'}
              </span>
            )}
          </div>
          {lagsta && (
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Lägsta saldo under året</div>
              <div style={{ fontSize: 19, fontWeight: 700, color: lagsta.utgaende < 0 ? '#A32D2D' : '#1D6A5F' }}>
                {kr0(lagsta.utgaende).toLocaleString('sv-SE')} tkr
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {MANAD_LABELS[manader.indexOf(lagsta.ym)]} {lagsta.ym.slice(2, 4)}
              </div>
            </div>
          )}
        </div>
        <div className="note">
          Ingående skulder betalas i årets första månad. Junis fakturor och löner ligger i föregående
          räkenskapsårs budget och fångas inte av förskjutningen, så juli blir annars för billig.
          Knappen hämtar {manadNamn(foreMan)} ur Qlik — det är <strong>Qliks lönemått</strong>, alltså lön plus
          lagstadgad arbetsgivaravgift. Lägg till ca 4 % för pension, Fora och särskild löneskatt om din
          personalrad har det påslaget. Har du byggt personalraden med <em>Förskjut 1 mån</em> påslaget är
          raden redan betalningstajmad — sätt då personal till <em>samma månad</em> och lämna fältet tomt,
          annars dubbelräknas junis löner.
        </div>
      </div>

      {/* Betalningsregler */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Betalningsregler</div>
          <button onClick={() => setVisaRegler((v) => !v)}
            style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 12px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>
            {visaRegler ? 'Dölj' : 'Visa'}
          </button>
        </div>
        {visaRegler && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 6, marginTop: 10 }}>
            {raderDef.filter((r) => r.grupp !== 'likvid').map((r) => {
              const nu = (likvid?.betalregler || {})[r.id] || DEFAULT_BETALREGEL[r.grupp] || 'samma';
              return (
                <label key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center', fontSize: 11.5, fontFamily: 'system-ui,sans-serif' }}>
                  <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
                  <select value={nu} disabled={last}
                    onChange={(e) => satt('betalregler', { ...(likvid?.betalregler || {}), [r.id]: e.target.value })}
                    style={{ border: '1px solid var(--border)', borderRadius: 5, padding: '2px 4px', fontSize: 11, background: 'var(--card)', color: 'var(--text)', fontFamily: 'system-ui,sans-serif' }}>
                    {BETALREGLER.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                  </select>
                </label>
              );
            })}
          </div>
        )}
        <div className="note">
          Personal ligger på <strong>+1</strong>: budgetradens julivärde är juli månads <em>arbete</em> (avstämt mot
          Qlik), och lönen lämnar kontot månaden efter. Byggde du raden med <em>Förskjut 1 mån</em> påslaget är den
          i stället redan betalningstajmad — sätt då personal till <em>samma månad</em>. Leverantörsfakturor bokförs
          på fakturadatum och betalas ca 30 dagar senare, därav +1. Avskrivningar har ingen kassaeffekt alls —
          motsvarigheten här är raden Investeringar.
        </div>
      </div>

      {/* Förskott */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Förskott och kampanjer</div>
          {!last && (
            <button onClick={() => setNyForskott({ rad: intaktRader[0]?.id || '', belopp: 0, betalMan: manader[0], intjanadFran: manader[0], intjanadTill: manader[0], kommentar: '' })}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
              + Lägg till
            </button>
          )}
        </div>
        {forskott.length === 0 && !nyForskott && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
            Inga förskott inlagda. En träningsgrupp i november som betalar 200 tkr redan i oktober läggs in här —
            budgeten står kvar i november, men kassan flyttas till oktober.
          </div>
        )}
        {forskott.length > 0 && (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              {['Rad', 'Belopp (tkr)', 'Betalas', 'Intjänas från', 'till', 'Kommentar', ''].map((h, i) => (
                <th key={i} style={{ ...th, textAlign: 'left' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {forskott.map((f, i) => (
                <tr key={f.id || i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...tdCalc, textAlign: 'left' }}>{raderDef.find((r) => r.id === f.rad)?.label || f.rad}</td>
                  <td style={tdCalc}>{kr0(f.belopp).toLocaleString('sv-SE')}</td>
                  <td style={{ ...tdCalc, textAlign: 'left' }}>{manadNamn(f.betalMan)}</td>
                  <td style={{ ...tdCalc, textAlign: 'left' }}>{manadNamn(f.intjanadFran)}</td>
                  <td style={{ ...tdCalc, textAlign: 'left' }}>{manadNamn(f.intjanadTill)}</td>
                  <td style={{ ...tdCalc, textAlign: 'left', color: 'var(--text-secondary)' }}>{f.kommentar || ''}</td>
                  <td style={tdCalc}>
                    {!last && (
                      <button onClick={() => satt('forskott', forskott.filter((_, j) => j !== i))}
                        style={{ background: 'transparent', border: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 13 }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {nyForskott && (
          <div style={{ marginTop: 10, padding: 10, background: 'var(--surface-2)', borderRadius: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', fontFamily: 'system-ui,sans-serif', fontSize: 11.5 }}>
            <Falt label="Rad">
              <select value={nyForskott.rad} onChange={(e) => setNyForskott({ ...nyForskott, rad: e.target.value })}
                style={{ border: '1px solid var(--border)', borderRadius: 5, padding: '3px 5px', fontSize: 11.5, background: 'var(--card)', color: 'var(--text)' }}>
                {raderDef.filter((r) => r.grupp !== 'likvid').map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </Falt>
            <Falt label="Belopp (tkr)">
              <input type="number" style={{ ...cellInp, width: 80 }} value={nyForskott.belopp ? kr0(nyForskott.belopp) : ''}
                onChange={(e) => setNyForskott({ ...nyForskott, belopp: Math.round((Number(e.target.value) || 0) * 1000) })} />
            </Falt>
            <Falt label="Betalas"><ManadVal manader={manader} utvidga value={nyForskott.betalMan} onChange={(v) => setNyForskott({ ...nyForskott, betalMan: v })} /></Falt>
            <Falt label="Intjänas från"><ManadVal manader={manader} value={nyForskott.intjanadFran} onChange={(v) => setNyForskott({ ...nyForskott, intjanadFran: v })} /></Falt>
            <Falt label="till"><ManadVal manader={manader} value={nyForskott.intjanadTill} onChange={(v) => setNyForskott({ ...nyForskott, intjanadTill: v })} /></Falt>
            <Falt label="Kommentar">
              <input type="text" value={nyForskott.kommentar} placeholder="Träningsgrupp, vinterkampanj…"
                onChange={(e) => setNyForskott({ ...nyForskott, kommentar: e.target.value })}
                style={{ border: '1px solid var(--border)', borderRadius: 5, padding: '3px 6px', fontSize: 11.5, background: 'var(--card)', color: 'var(--text)', width: 180 }} />
            </Falt>
            <button onClick={() => { satt('forskott', [...forskott, { ...nyForskott, id: String(Date.now()) }]); setNyForskott(null); }}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>Spara</button>
            <button onClick={() => setNyForskott(null)}
              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--text)' }}>Avbryt</button>
          </div>
        )}
        <div className="note">
          Beloppet dras från intjänandemånaderna och läggs till i betalmånaden, viktat mot budgeterad intäkt.
          Årssumman ändras aldrig — pengarna flyttas bara. Betalmånader före räkenskapsårets start hamnar
          utanför tabellen, vilket är rätt: de pengarna kom ett annat år.
        </div>
      </div>

      {/* Själva matrisen */}
      <div className="card">
        <div className="card-title">Likviditetsmatris — {ra.slice(0, 2)}/{ra.slice(2)}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 900 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--card)', zIndex: 2 }} />
              {manader.map((m, i) => <th key={m} style={th}>{MANAD_LABELS[i]}</th>)}
              <th style={th}>Summa</th>
            </tr></thead>
            <tbody>
              {summaRad('Ingående saldo', perManad.map((p) => p.ingaende), { utanSumma: true, bg: 'var(--surface-2)', farga: true })}

              <tr><td colSpan={manader.length + 2} style={{ paddingTop: 10, fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', fontFamily: 'system-ui,sans-serif' }}>Inbetalningar</td></tr>
              {intaktRader.map((r) => flodesRad(r, 1))}
              {likvidIn.map(likvidRad)}
              {summaRad('Summa in', perManad.map((p) => p.inbet))}

              <tr><td colSpan={manader.length + 2} style={{ paddingTop: 10, fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', fontFamily: 'system-ui,sans-serif' }}>Utbetalningar</td></tr>
              {[['Ingående lev.skulder', likvid?.ingaendeSkulder], [`Ingående personalskuld (${manadNamn(foreMan)})`, likvid?.ingaendePersonal]]
                .filter(([, belopp]) => belopp)
                .map(([label, belopp]) => (
                  <tr key={label}>
                    <td style={tdLabel}>{label}</td>
                    {manader.map((m, i) => (
                      <td key={m} style={{ ...tdCalc, color: 'var(--text-secondary)' }}>{i === 0 ? visa(belopp) : '–'}</td>
                    ))}
                    <td style={{ ...tdCalc, fontWeight: 600 }}>{visa(belopp)}</td>
                  </tr>
                ))}
              {kostnadRader.map((r) => flodesRad(r, -1))}
              {likvidUt.map(likvidRad)}
              {summaRad('Summa ut', perManad.map((p) => p.utbet))}

              {summaRad('Månadens kassaflöde', perManad.map((p) => p.netto), { bg: 'var(--surface-2)', farga: true })}
              {avstMan && summaRad('Avstämning mot bank', perManad.map((p) => p.justering), { utanSumma: true, farga: true })}
              {summaRad('Utgående saldo', perManad.map((p) => p.utgaende), { utanSumma: true, bg: 'var(--surface-2)', farga: true })}
            </tbody>
          </table>
        </div>
        <div className="note">
          Alla belopp i tkr. Rörelseraderna härleds ur budgetmatrisen och går inte att redigera här — ändra
          i budgeten eller justera betalningsregeln. <strong>Momsen ingår inte än</strong>, så saldot är
          missvisande under och strax efter säsongen: momsen på en stark månad betalas månaden efter, ofta
          när intäkterna redan fallit. Avskrivningar är utelämnade, investeringar tillagda.
        </div>
      </div>
    </>
  );
}

// Fälten matas i tkr men lagras i kronor. Skriv ut kronbeloppet under
// rutan — en nolla för mycket syns då direkt i stället för att smyga in
// som en osynlig post i Summa ut.
function Kvitto({ belopp }) {
  const kr = belopp || 0;
  if (!kr) return null;
  const orimligt = Math.abs(kr) >= 100000000; // ≥ 100 mkr — nästan säkert en felskrivning
  return (
    <span style={{ fontSize: 10, color: orimligt ? '#A32D2D' : 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
      = {kr.toLocaleString('sv-SE')} kr{orimligt ? ' ⚠' : ''}
    </span>
  );
}

function Falt({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>{label}</span>
      {children}
    </label>
  );
}

// Månadsväljare. utvidga=true lägger till de tre månaderna före årets start,
// så förskott betalda i förra räkenskapsåret går att registrera.
function ManadVal({ manader, value, onChange, utvidga }) {
  const val = utvidga
    ? [...[3, 2, 1].map((n) => backat(manader[0], n)), ...manader]
    : manader;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      style={{ border: '1px solid var(--border)', borderRadius: 5, padding: '3px 5px', fontSize: 11.5, background: 'var(--card)', color: 'var(--text)' }}>
      {val.map((m) => <option key={m} value={m}>{manadNamn(m)}</option>)}
    </select>
  );
}

function backat(ym, n) {
  let y = Number(ym.slice(0, 4)), m = Number(ym.slice(4)) - n;
  while (m < 1) { m += 12; y -= 1; }
  return `${y}${String(m).padStart(2, '0')}`;
}

function manadNamn(ym) {
  if (!ym) return '';
  return `${MANAD_LABELS[(Number(ym.slice(4)) + 5) % 12]} ${ym.slice(2, 4)}`;
}
